import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { QUERY_KEYS } from "@/constants";
import {
    DEPARTURE_ACTION_TO_CHECKPOINT,
    DEPARTURE_ACTION_TO_HOTEL_CLINIC_STATUS,
    DepartureProcessItem,
} from "@/pages/HotelPlanning/departure/utils/process";
import { useUpdateHotelClinicTasksMutation } from "@/requests/hotel-clinic.mutations";
import { useHotelDepartureProgressMutation } from "@/requests/hotel-departure.queries";
import type { ICommonListResponse } from "@/schemas/commonList";

function useHotelDepartureProgress() {
    const { mutateAsync: progressCheckpoint, isPending: checkpointPending } = useHotelDepartureProgressMutation();
    // isoDate undefined → skip its clinic-arrival invalidation; we invalidate the departure queries below.
    const { mutateAsync: updateCustomTasks, isPending: taskPending } = useUpdateHotelClinicTasksMutation({
        isoDate: undefined,
    });
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const isPending = checkpointPending || taskPending;

    const handleHotelDepartureProgress = async (items: DepartureProcessItem[]) => {
        if (isPending || !items.length) return;

        const clinic = items.filter((i) => i.type === "clinic");
        const custom = items.filter((i) => i.type === "custom");
        // A single trigger (row button or bulk menu item) is homogeneous in action.
        const action = items[0].action;
        const textFlag = action === "start" ? "departure" : "complete";

        // Map each mutation's per-item response `id` back to its grid row id.
        const keyToRowId = new Map<string, string>();
        const succeeded: string[] = [];
        let failed = 0;

        const collect = (responses: ICommonListResponse) => {
            for (const r of responses) {
                const rowId = keyToRowId.get(r.id) ?? r.id;
                if (r.ok) {
                    succeeded.push(rowId);
                } else {
                    failed += 1;
                    enqueueSnackbar(r.message || "", { variant: "error" });
                }
            }
        };

        const runBucket = async (p: Promise<ICommonListResponse>) => {
            try {
                collect(await p);
            } catch (error) {
                console.error("#handleHotelDepartureProgress", error);
                if (error instanceof Error) enqueueSnackbar(error.message, { variant: "error" });
            }
        };

        const buckets: Promise<void>[] = [];

        if (clinic.length) {
            clinic.forEach((i) => keyToRowId.set(i.operationId, i.rowId));
            buckets.push(
                runBucket(
                    progressCheckpoint({
                        patients: clinic.map((i) => ({
                            operationId: i.operationId,
                            uniqueCheckpointId: i.uniqueCheckpointId,
                            action: DEPARTURE_ACTION_TO_CHECKPOINT[i.action],
                        })),
                    })
                )
            );
        }

        if (custom.length) {
            const status = DEPARTURE_ACTION_TO_HOTEL_CLINIC_STATUS[action];
            custom.forEach((i) => keyToRowId.set(i.taskId, i.rowId));
            buckets.push(
                runBucket(
                    // Departure pool → uniqueTask keeps each passenger's task single (skips pickup-spread check).
                    updateCustomTasks({
                        tasks: custom.map((i) => ({ taskId: i.taskId, updateData: { status } })),
                        uniqueTask: true,
                    })
                )
            );
        }

        await Promise.all(buckets);

        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_PLANNING] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_HOTEL_DEPARTURE_CUSTOM_PASSENGERS] });

        if (succeeded.length && !failed) {
            enqueueSnackbar(t(`responses.bulkHotelDepartureProgress.${textFlag}`, { count: succeeded.length }), {
                variant: "success",
            });
        }
    };

    return { handleHotelDepartureProgress, isPending };
}

export default useHotelDepartureProgress;
